/**
 * PageFAB — kontextová "rychlá akce" plovoucí tlačítko v pravém dolním rohu
 * (vedle AI asistenta). Akce se dynamicky mění podle aktuální stránky.
 *
 * API:
 *   // V AdminLayout.js:
 *   <PageActionsProvider>
 *     <Routes>...</Routes>
 *     <PageFAB />
 *   </PageActionsProvider>
 *
 *   // V každé stránce:
 *   const setActions = usePageActions();
 *   useEffect(() => setActions([
 *     { label: "Nový zákazník", icon: Plus, onClick: () => setCreateOpen(true), primary: true },
 *     { label: "Importovat CSV", icon: Upload, onClick: () => ... },
 *   ]), []);
 */
import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef,
} from "react";
import { createPortal } from "react-dom";
import { Plus, X, Zap } from "lucide-react";

/* ────────────────────────────────────────────────────────────────────
   Context + hook
   ──────────────────────────────────────────────────────────────────── */
const PageActionsCtx = createContext({ actions: [], setActions: () => {} });

export function PageActionsProvider({ children }) {
  const [actions, setActions] = useState([]);
  return (
    <PageActionsCtx.Provider value={{ actions, setActions }}>
      {children}
    </PageActionsCtx.Provider>
  );
}

/**
 * Register FAB actions for the current page. Pass a list of action objects;
 * auto-cleared when the component unmounts (i.e. user navigates away).
 *
 *   usePageActions([
 *     { label: "Nový", icon: Plus, onClick: ..., primary: true },
 *     { divider: true },
 *     { label: "Export CSV", icon: Download, onClick: ... },
 *   ], [deps]);
 */
export function usePageActions(actions, deps = []) {
  const { setActions } = useContext(PageActionsCtx);
  useEffect(() => {
    setActions(actions || []);
    return () => setActions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* ────────────────────────────────────────────────────────────────────
   PageFAB — render the button + expandable menu
   ──────────────────────────────────────────────────────────────────── */
export default function PageFAB() {
  const { actions } = useContext(PageActionsCtx);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!actions || actions.length === 0) return null;

  // iter409d — FAB main button removed (user request: "Rychlý záznam překáží").
  // Page-level "Přidat" actions remain accessible via:
  //   - in-module "+ Nová X" buttons in module headers
  //   - right-click context menu on desktop
  //   - keyboard shortcuts (Ctrl+J)
  // The component is kept rendered (returns null) so usePageActions() callers
  // don't break; actions are still registered into PageActionsCtx for future use.
  return null;
}
